import styled, { keyframes } from 'styled-components'

// ── paleta (coerente com as demais telas do sistema) ──────────────
const TEAL = 'var(--teal)'
const TEAL_TEXT = 'var(--teal-text, var(--teal))';
const TEAL_LIGHT = 'var(--teal-light)'
const TEAL_DEEP = '#00313f'
const TEAL_SOFT = 'var(--teal-soft)'
const GRAY_BG = 'var(--bg)'
const GRAY_LINE = 'var(--line)'
const GRAY_TEXT = 'var(--text-muted)'
const RED = 'var(--red, #dc2626)'
const RED_SOFT = 'var(--red-soft)'
const RED_LINE = 'var(--red)'

const bp = { tablet: '900px', mobile: '540px', small: '380px' }

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`

// === CONTAINER GERAL (split institucional) ===
export const Container = styled.div`
  width: 100%;
  /* O App já tem a barra superior (.logo) acima deste container.
     Aqui ocupamos o espaço restante via flex do .container pai,
     sem 100vh para não estourar a altura e gerar scroll. */
  flex: 1;
  align-self: stretch;
  min-height: 0;
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  background: ${GRAY_BG};
  box-sizing: border-box;

  @media (max-width: ${bp.tablet}) {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
  }
`

// painel institucional (esquerda no desktop, topo no mobile)
export const Brand = styled.aside`
  background: linear-gradient(160deg, ${TEAL} 0%, ${TEAL_DEEP} 100%);
  color: #fff;
  padding: 56px 48px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 40px;
  position: relative;
  overflow: hidden;

  /* textura sutil: arco luminoso */
  &:after {
    content: '';
    position: absolute;
    right: -120px;
    bottom: -120px;
    width: 360px;
    height: 360px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%);
  }

  @media (max-width: ${bp.tablet}) {
    padding: 18px 22px;
    gap: 0;
    flex-direction: row;
    align-items: center;
    justify-content: center;
  }
  @media (max-width: ${bp.mobile}) {
    padding: 16px 18px;
  }
`

// marca: átomo + nome do sistema, lado a lado
export const BrandMark = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
  z-index: 1;

  /* nome do sistema ao lado da logo */
  span {
    font-size: 18px;
    font-weight: 800;
    letter-spacing: 0.2px;
    line-height: 1.15;
    color: #fff;
  }

  /* o SVG do átomo não encolhe */
  svg { flex-shrink: 0; }

  @media (max-width: ${bp.tablet}) {
    gap: 10px;
    span { font-size: 16px; }
  }
  @media (max-width: ${bp.small}) {
    span { font-size: 15px; }
  }
`

export const BrandBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  position: relative;
  z-index: 1;

  @media (max-width: ${bp.tablet}) { display: none; }
`

export const BrandTitle = styled.h1`
  margin: 0;
  font-size: clamp(26px, 3.2vw, 40px);
  font-weight: 800;
  line-height: 1.12;
  letter-spacing: -0.4px;

  @media (max-width: ${bp.tablet}) { font-size: 18px; }
`

export const BrandSubtitle = styled.p`
  margin: 0;
  font-size: clamp(14px, 1.4vw, 16px);
  line-height: 1.5;
  color: rgba(255,255,255,0.82);
  max-width: 38ch;

  @media (max-width: ${bp.tablet}) { display: none; }
`

export const BrandFoot = styled.span`
  font-size: 12px;
  color: rgba(255,255,255,0.6);
  position: relative;
  z-index: 1;

  @media (max-width: ${bp.tablet}) { display: none; }
`

// === LADO DO FORMULÁRIO ===
export const FormSide = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  box-sizing: border-box;

  @media (max-width: ${bp.mobile}) { padding: 24px 16px; }
`

// === FORMULÁRIO ===
export const InputArea = styled.form`
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  animation: ${fadeUp} .4s ease;
`

export const FormHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 6px;
`

export const FormTitle = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color: ${TEAL_TEXT};
  letter-spacing: -0.2px;
`

export const FormHint = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${GRAY_TEXT};
`

// rótulo + campo
export const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  font-weight: 700;
  color: ${GRAY_TEXT};
  text-transform: uppercase;
  letter-spacing: 0.3px;
`

// === CAMPOS DE INPUT ===
export const Area = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 52px;
  background: var(--surface);
  border: 1.5px solid ${({ $error }) => ($error ? RED_LINE : GRAY_LINE)};
  border-radius: 12px;
  padding: 0 14px;
  box-sizing: border-box;
  transition: border-color .15s ease, box-shadow .15s ease;

  &:focus-within {
    border-color: ${TEAL_LIGHT};
    box-shadow: 0 0 0 3px rgba(10, 90, 115, 0.10);
  }

  svg { color: ${GRAY_TEXT}; flex-shrink: 0; }
`

export const Input = styled.input`
  width: 100%;
  font-size: 16px; /* 16px evita zoom automático no iOS */
  background: transparent;
  border: none;
  outline: none;
  padding: 12px 0;
  color: ${TEAL_TEXT};
  font-family: inherit;

  &::placeholder { color: #9aa5aa; }
`

// botão de mostrar/ocultar senha
export const EyeButton = styled.button`
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${GRAY_TEXT};
  padding: 6px;
  border-radius: 8px;
  flex-shrink: 0;

  &:hover { color: ${TEAL_TEXT}; }
  &:focus-visible { outline: 2px solid ${TEAL_LIGHT}; outline-offset: 2px; }
`

// === BOTÃO PRINCIPAL ===
export const Btt = styled.button`
  width: 100%;
  min-height: 52px;
  margin-top: 4px;
  background: ${TEAL};
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  border-radius: 12px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  transition: filter .15s ease, transform .12s ease;

  &:hover:not(:disabled) { filter: brightness(1.12); transform: translateY(-1px); }
  &:active:not(:disabled) { transform: translateY(0); }
  &:disabled { opacity: .5; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid ${TEAL_LIGHT}; outline-offset: 2px; }
`

// === AGRUPADOR DOS BOTÕES SUPERIORES ===
export const TopButtons = styled.div`
  align-self: flex-end;
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: ${bp.mobile}) { align-self: stretch; }
`

// === BOTÃO SUPERIOR (SEMED) ===
export const AccessTopButton = styled.button`
  padding: 10px 18px;
  border: 1.5px solid ${GRAY_LINE};
  border-radius: 10px;
  background: var(--surface);
  color: ${TEAL_TEXT};
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;
  transition: background .2s ease, border-color .2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;

  &:hover { background: ${TEAL_SOFT}; border-color: ${TEAL_SOFT}; }
  &:focus-visible { outline: 2px solid ${TEAL_LIGHT}; outline-offset: 2px; }

  @media (max-width: ${bp.mobile}) { align-self: stretch; justify-content: center; }
`

// === MENSAGEM DE ERRO ===
export const ErrorBanner = styled.div`
  background: ${RED_SOFT};
  border: 1px solid ${RED_LINE};
  color: ${RED};
  border-radius: 10px;
  padding: 11px 13px;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  animation: ${fadeUp} .2s ease;
`

// === "ESQUECEU SENHA" ===
export const SignMessageButton = styled.button`
  background: none;
  border: none;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  font-size: 14px;
  color: ${GRAY_TEXT};
  margin-top: 2px;
  align-self: center;

  &:hover span:last-child { text-decoration: underline; }
  &:focus-visible { outline: 2px solid ${TEAL_LIGHT}; outline-offset: 2px; border-radius: 6px; }
`

export const SignMessageButtonText = styled.span`
  margin-right: 2px;
`

export const SignMessageButtonTextBold = styled.span`
  font-weight: 700;
  color: ${TEAL_TEXT};
  transition: 0.3s;
`